import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { SkillArchitectureService } from '../../skill-architecture/services/skill-architecture.service';
import {
    Parameter,
    SkillArchitectureDTO,
} from '../../skill-architecture/models/skill-architecture-form';
import {
    ArchitectureType,
    PretrainedWeights,
} from './models/image-feature-extractor-form';
import { DropdownEntry } from '../../shared/components/dropdown/dropdown.component';
import { FormControl, FormGroup } from '@angular/forms';
import { nonNil } from '../../shared/utility';
import { AlgorithmValidator } from '../../algorithm/validators/algorithm-validator';

@Component({
    selector: 'app-image-feature-extractor',
    templateUrl: './image-feature-extractor.component.html',
    styleUrls: ['./image-feature-extractor.component.scss'],
    standalone: false,
})
export class ImageFeatureExtractorComponent implements OnInit, OnChanges {
    @Input() trainingId: string;
    @Input() editingIsDisabled: boolean;

    imageFeatureExtractor: Parameter[];

    useImages: boolean;
    architectureType: string;
    pretrainedWeights: string;
    compatibilityLayerSize: number;

    supportedArchitectureTypes: DropdownEntry[];
    supportedPretrainedWeights: DropdownEntry[];

    form: FormGroup;

    initializationFinished: boolean = false;

    constructor(private skillArchitectureService: SkillArchitectureService) {}

    ngOnInit(): void {
        this.skillArchitectureService
            .getSkillArchitectureFormData(this.trainingId)
            .subscribe((skillArchitecture: SkillArchitectureDTO) => {
                this.imageFeatureExtractor = skillArchitecture.parameter;

                this.extractValues(this.imageFeatureExtractor);
                this.setSupportedConfigs();
                this.createForm();

                this.initializationFinished = true;
            });
    }

    private createForm() {
        this.form = new FormGroup({
            useImages: new FormControl<boolean>(this.useImages),
            architectureType: new FormControl<string>(this.architectureType),
            pretrainedWeights: new FormControl<string>(this.pretrainedWeights),
            compatibilityLayerSize: this.createCompatibilityLayerSizeControl(),
        });

        if (this.editingIsDisabled) {
            this.form.disable();
        } else if (!this.useImages) {
            this.disableControlsBasedOnUseImages();
        }
    }

    private disableControlsBasedOnUseImages() {
        [
            'architectureType',
            'pretrainedWeights',
            'compatibilityLayerSize',
        ].forEach((controlName) => {
            this.form.get(controlName).disable();
        });
    }

    private enableControlsBasedOnUseImages() {
        [
            'architectureType',
            'pretrainedWeights',
            'compatibilityLayerSize',
        ].forEach((controlName) => {
            this.form.get(controlName).enable();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.editingIsDisabled && this.initializationFinished) {
            if (this.editingIsDisabled) {
                this.form.disable();
            } else {
                this.form.enable();
                this.disableControlsBasedOnUseImages();
            }
        }
    }

    private extractValues(imageFeatureExtractor: Parameter[]) {
        imageFeatureExtractor.forEach((param: Parameter) => {
            if (param.name === 'useImages') {
                this.useImages = param.value;
            }
            if (param.name === 'architectureType') {
                this.architectureType = param.value;
            }
            if (param.name === 'pretrainedWeights') {
                this.pretrainedWeights = param.value;
            }
            if (param.name === 'compatibilityLayerSize') {
                this.compatibilityLayerSize = param.value;
            }
        });
    }

    private setSupportedConfigs() {
        this.setSupportedArchitectures();
        this.setSupportedWeights();
    }

    private setSupportedArchitectures() {
        this.supportedArchitectureTypes = Object.entries(ArchitectureType).map(
            (entry) => {
                return {
                    value: entry[0],
                    label: entry[1],
                } as DropdownEntry;
            }
        );
    }

    private setSupportedWeights() {
        let entries = Object.entries(PretrainedWeights);

        if (this.architectureType !== 'RESNET50') {
            entries = entries.filter(([key]) => key === 'not_pretrained');
        }

        this.supportedPretrainedWeights = entries.map(([key, value]) => {
            return {
                value: key,
                label: value,
            } as DropdownEntry;
        });

        const currentWeights = this.supportedPretrainedWeights.map(
            (entry) => entry.value
        );

        if (!currentWeights.includes(this.pretrainedWeights)) {
            this.pretrainedWeights = currentWeights[0];
        }
    }

    private createCompatibilityLayerSizeControl(): FormControl<string> {
        const controlValue = nonNil(this.compatibilityLayerSize)
            ? this.compatibilityLayerSize.toString()
            : '';

        return new FormControl<string>(controlValue, {
            validators: [AlgorithmValidator.validatePositiveInteger],
        });
    }

    onUseImagesCheckboxChange(checked: boolean) {
        this.useImages = checked;

        if (!this.useImages) {
            this.disableControlsBasedOnUseImages();
        } else {
            this.enableControlsBasedOnUseImages();
        }

        this.saveChanges();
    }

    onSelectedArchitectureTypeChanged(newValue: string) {
        this.architectureType = newValue;
        this.setSupportedWeights();
        this.saveChanges();
    }

    onSelectedPretrainedWeightsChanged(newValue: string) {
        this.pretrainedWeights = newValue;
        this.saveChanges();
    }

    onCompatibilityLayerSizeChange() {
        this.compatibilityLayerSize = this.convertToNumber(
            this.form.get('compatibilityLayerSize').value
        );
        this.saveChanges();
    }

    private convertToNumber(controlValue: any): number {
        if (controlValue === '') {
            return null;
        }
        return parseInt(controlValue);
    }

    private saveChanges() {
        const newParams = this.imageFeatureExtractor.map((param: Parameter) => {
            if (param.name === 'useImages') {
                param.value = this.useImages;
            } else if (param.name === 'architectureType') {
                param.value = this.architectureType;
            } else if (param.name === 'pretrainedWeights') {
                param.value = this.pretrainedWeights;
            } else if (param.name === 'compatibilityLayerSize') {
                param.value = this.compatibilityLayerSize;
            }
            return param;
        });

        const dto = {
            parameter: newParams,
        } as SkillArchitectureDTO;

        this.skillArchitectureService
            .saveSkillArchitectureFormData(this.trainingId, dto)
            .subscribe();
    }
}
